// Implements: F-IDN-027
/**
 * The one-field dialog that names a team or an organization (v3 `nameDialog`): 1 to 120 characters
 * after trimming, said under the field before anything is sent. A refusal of the server is shown
 * in the same place and the dialog stays open.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, errorId } from "../../../shared/ui/molecules/Field";
import { Dialog } from "../../../shared/ui/organisms/Dialog";

/** The bound of every name here (`OrganizationCreateRequest`, `TeamCreateRequest`: 1..120). */
export const NAME_MAX_LENGTH = 120;

export interface NameDialogProps {
  readonly title: string;
  readonly label: string;
  readonly submitLabel: string;
  readonly initial?: string | undefined;
  readonly pending: boolean;
  /** The server's refusal, already in the person's words, or `null`. */
  readonly problem: string | null;
  readonly onSubmit: (name: string) => void;
  readonly onCancel: () => void;
}

export function NameDialog({
  title,
  label,
  submitLabel,
  initial = "",
  pending,
  problem,
  onSubmit,
  onCancel,
}: NameDialogProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const [name, setName] = useState(initial);
  const [invalid, setInvalid] = useState<string | null>(null);
  const shown = invalid ?? problem ?? undefined;

  function submit(): void {
    if (pending) return;
    const trimmed = name.trim();
    if (trimmed === "") {
      setInvalid(t("organization.name.required"));
      return;
    }
    if (trimmed.length > NAME_MAX_LENGTH) {
      setInvalid(t("organization.name.tooLong"));
      return;
    }
    setInvalid(null);
    onSubmit(trimmed);
  }

  return (
    <Dialog
      open
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t("common.action.cancel")}
          </Button>
          <Button variant="primary" loading={pending} onClick={submit}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <Field id={id} label={label} error={shown}>
        <Input
          id={id}
          value={name}
          invalid={shown !== undefined}
          describedBy={shown === undefined ? undefined : errorId(id)}
          onChange={(value) => {
            setName(value);
            setInvalid(null);
          }}
          onEnter={submit}
        />
      </Field>
    </Dialog>
  );
}
